import {
  createContext,
  ReactNode,
  useContext,
  useMemo,
  useState,
} from 'react';
import { useTransformContext, useTransformEffect } from 'react-zoom-pan-pinch';

const DEFAULT_MIN_COUNTER_SCALE = 0.3;
const DEFAULT_MAX_COUNTER_SCALE = 2;

type CounterScaleContextValue = {
  rawCounterScale: number;
};

const CounterScaleContext = createContext<
  CounterScaleContextValue | undefined
>(undefined);

export function CounterScaleProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const {
    transformState: { scale },
    props: { initialScale },
  } = useTransformContext();
  const [currentScale, setCurrentScale] = useState(scale);

  useTransformEffect(({ state }) => {
    setCurrentScale(state.scale);
  });

  const value = useMemo((): CounterScaleContextValue => {
    const zoomFactor = currentScale / (initialScale || 1);
    return { rawCounterScale: 1 / zoomFactor };
  }, [currentScale, initialScale]);

  return (
    <CounterScaleContext.Provider value={value}>
      {children}
    </CounterScaleContext.Provider>
  );
}

export function useCounterScale(options?: {
  minScale?: number;
  maxScale?: number;
}): number {
  const context = useContext(CounterScaleContext);
  if (context === undefined) {
    throw new Error(
      'useCounterScale must be used within CounterScaleProvider'
    );
  }

  const {
    minScale = DEFAULT_MIN_COUNTER_SCALE,
    maxScale = DEFAULT_MAX_COUNTER_SCALE,
  } = options ?? {};

  return Math.max(
    minScale,
    Math.min(maxScale, context.rawCounterScale)
  );
}
